import React from "react";
import { cn } from "../../utils/utils";

const isAppWarm = typeof window !== 'undefined' && sessionStorage.getItem('home_intro_played');

export function Skeleton({ className, ...props }) {
  const isAppWarm = typeof window !== 'undefined' && sessionStorage.getItem('home_intro_played');
  
  return (
    <div
      className={cn(
        "bg-[var(--color-surface-hover)] rounded-md", 
        !isAppWarm && "animate-pulse", // Only pulse if not warm or keep pulse? User said "make app look more slow". Pulses are fine usually, but fade-ins are the issue.
        isAppWarm && "opacity-50", // Static for warm re-visits to feel faster
        className
      )}
      {...props}
    />
  );
}

// Home Page Expense List Skeleton
export function HomeExpenseSkeleton() {
  return (
    <div className="mt-4">
      {[1, 2].map((group) => (
        <div key={group} className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center px-2 mb-2.5">
            <Skeleton className="w-24 h-4 rounded" />
            <Skeleton className="w-16 h-4 rounded" />
          </div>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between bg-[var(--color-surface)] px-3.5 sm:px-4 py-3 sm:py-3.5 rounded-2xl shadow-sm mb-2.5"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1 pr-2">
                <Skeleton className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex-shrink-0" />
                <div className="space-y-1.5 flex-1 max-w-[120px]">
                  <Skeleton className="w-full h-4 sm:h-5 rounded" />
                  <Skeleton className="w-2/3 h-3 sm:h-4 rounded" />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Skeleton className="w-16 sm:w-20 h-4 sm:h-5 rounded" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Reports Page Skeleton
export function ReportsSkeleton() {
  const isAppWarm = typeof window !== 'undefined' && sessionStorage.getItem('home_intro_played');

  return (
    <div className={cn("w-full font-sans text-[#f0f0f0]", !isAppWarm && "animate-in fade-in duration-500")}>
      
      {/* Timeframe selector mock */}
      <div className="max-w-xl mx-auto mb-6 flex justify-center p-1 bg-[#1A1A1A] rounded-full mx-4 sm:mx-auto">
        {[1,2,3,4].map(i => <Skeleton key={i} className="flex-1 h-8 rounded-full mx-1" />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 px-4 lg:px-0">
        <div className="lg:col-span-1">
          {/* Total Spent Mock */}
          <div className="bg-[#151515] rounded-3xl p-6 h-full min-h-[180px] flex flex-col justify-between">
             <Skeleton className="w-24 h-4 rounded mb-4" />
             <div>
                <Skeleton className="w-40 h-10 rounded mb-2" />
                <Skeleton className="w-32 h-4 rounded" />
             </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          {/* Trend Chart Mock */}
          <div className="bg-[#151515] rounded-3xl p-6 h-full min-h-[220px] flex flex-col justify-between">
             <Skeleton className="w-32 h-5 rounded mb-6" />
             <div className="flex items-end justify-between h-[120px] gap-2">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="w-8 sm:w-12 rounded-t-md" style={{ height: `${Math.max(30, Math.random() * 100)}%` }} />
                ))}
             </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 lg:px-0 mb-20">
        <div className="lg:col-span-1">
          {/* Insights Mock */}
          <div className="bg-[#151515] rounded-3xl p-6 h-full">
            <Skeleton className="w-24 h-5 rounded mb-6" />
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="flex gap-3 items-center">
                  <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="w-full h-3 rounded" />
                    <Skeleton className="w-2/3 h-3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-1">
          {/* Top Expenses Mock */}
          <div className="bg-[#151515] rounded-3xl p-6 h-full">
            <Skeleton className="w-32 h-5 rounded mb-6" />
            <div className="space-y-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                     <Skeleton className="w-8 h-8 rounded-full" />
                     <Skeleton className="w-24 h-4 rounded" />
                  </div>
                  <Skeleton className="w-16 h-4 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-1">
          {/* Category Mock */}
          <div className="bg-[#151515] rounded-3xl p-6 h-full">
            <Skeleton className="w-36 h-5 rounded mb-6" />
            <div className="space-y-4">
              {[1,2,3,4].map(i => (
                <div key={i}>
                  <div className="flex justify-between mb-2">
                    <Skeleton className="w-20 h-3 rounded" />
                    <Skeleton className="w-12 h-3 rounded" />
                  </div>
                  <Skeleton className="w-full h-2 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// Search Page Skeleton
export function SearchListSkeleton() {
  return (
    <div className={cn("space-y-6", !isAppWarm && "animate-in fade-in duration-300")}>
      {[1, 2].map((group) => (
        <div key={group}>
          <div className="flex justify-between items-center px-2 mb-3">
            <Skeleton className="w-24 h-3 rounded" />
            <Skeleton className="w-16 h-3 rounded" />
          </div>
          <div className="space-y-2.5">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between bg-[var(--color-surface)] px-3.5 sm:px-4 py-3 sm:py-3.5 rounded-2xl shadow-sm border border-white/[0.02]"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1 pr-2">
                  <Skeleton className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex-shrink-0" />
                  <div className="space-y-1.5 flex-1 max-w-[120px]">
                    <Skeleton className="w-full h-4 sm:h-5 rounded" />
                    <Skeleton className="w-2/3 h-3 rounded" />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Skeleton className="w-14 h-4 sm:h-5 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Profile Page Skeleton
export function ProfilePageSkeleton() {
  return (
    <div className={cn("pb-16", !isAppWarm && "animate-in fade-in duration-500")}>
      {/* Avatar Section */}
      <div className="flex flex-col items-stretch gap-5">
         <div className="relative w-full rounded-[2rem] overflow-hidden shadow-lg bg-[var(--color-surface)] border border-white/[0.05] p-8 py-10 flex flex-col justify-center">
           <Skeleton className="w-48 h-8 rounded mb-3 translate-x-1" />
           <Skeleton className="w-24 h-4 rounded translate-x-1" />
         </div>
      </div>

      {/* Settings Sections */}
      <div className="mt-6 space-y-4">
        {[1, 2, 3].map((sectionIndex) => (
          <div key={sectionIndex} className="space-y-2 px-1">
             <Skeleton className="w-24 h-6 rounded mb-2" />
             {[1, 2].map((itemIndex) => (
                <div key={itemIndex} className="flex items-center justify-between bg-[var(--color-surface)] rounded-2xl p-4">
                  <div className="flex items-center space-x-3">
                     <Skeleton className="w-5 h-5 rounded" />
                     <div className="space-y-2">
                       <Skeleton className="w-28 h-4 rounded" />
                       <Skeleton className="w-40 h-3 rounded" />
                     </div>
                  </div>
                </div>
             ))}
          </div>
        ))}
      </div>
    </div>
  );
}
